import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';

@Injectable()
export class RequestAuthorizationInterceptor implements HttpInterceptor {

    constructor(private router: Router) {}

    intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
        const token = this.getAuthToken();

        if (!token) {
            // No auth param anywhere → leave request unchanged
            return next.handle(request);
        }

        return next.handle(
            request.clone({
                setHeaders: { Authorization: token }
            })
        );
    }

    /**
     * Try to read 'authorization' from:
     * 1) Angular Router (once navigation has happened)
     * 2) Hash fragment query (for '#/path?authorization=...')
     * 3) Normal query string before '#'
     * Returns decoded token (base64 → text) when possible.
     */
    private getAuthToken(): string | null {
        // 1) Router query params (works after initial navigation)
        try {
            const tree = this.router.parseUrl(this.router.url || '/');
            const fromRouter = tree.queryParams?.['authorization'];
            const decoded = this.decodeIfBase64(fromRouter);
            if (decoded) return decoded;
        } catch { /* ignore */ }

        // 2) Hash fragment query params (handles early bootstrap + hash routing)
        try {
            const hash = window.location.hash || ''; // e.g. "#/data_entry/...?...&authorization=ABC"
            const qIndex = hash.indexOf('?');
            if (qIndex !== -1) {
                const query = hash.substring(qIndex + 1);
                const params = new URLSearchParams(query);
                const fromHash = params.get('authorization') || undefined;
                const decoded = this.decodeIfBase64(fromHash);
                if (decoded) return decoded;
            }
        } catch { /* ignore */ }

        // 3) Normal query string before '#'
        try {
            const url = new URL(window.location.href);
            const fromSearch = url.searchParams.get('authorization') || undefined;
            const decoded = this.decodeIfBase64(fromSearch);
            if (decoded) return decoded;
        } catch { /* ignore */ }

        return null;
    }

    private decodeIfBase64(value?: string | null): string | null {
        if (!value) return null;
        try {
            // If it's valid base64, decode; if not, just use as-is
            return atob(value);
        } catch {
            return value;
        }
    }
}
